/**
 * Selector de «cómo entrará» en las altas que CREAN credenciales.
 *
 * Existe en un componente y no repetido en cada formulario porque son TRES los
 * sitios donde nace un acceso a un portal —alta de cuenta de cliente desde
 * admin, alta de cliente desde el portal partner y alta de partner— y las tres
 * tienen que ofrecer exactamente las mismas opciones con las mismas palabras: si
 * una se quedara atrás, habría altas que no pueden elegir Google sin que nadie lo
 * note. Es además el mismo texto que ya usa el alta de usuarios de la cuenta
 * (CustomerAccountPage), para que el operador aprenda el concepto una sola vez.
 *
 * La consecuencia de cada opción NO se explica en el label sino debajo, porque
 * lo que importa no es el nombre del método sino qué pasa: si se genera o no
 * contraseña temporal y qué correo recibirá esa persona.
 */
import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountUserAuthMethod } from '../types/clientAccount';

interface Props {
  value: AccountUserAuthMethod;
  onChange: (value: AccountUserAuthMethod) => void;
  /** A quién se da de alta: cambia solo el sujeto del texto de ayuda. */
  subject?: 'owner' | 'partner';
  disabled?: boolean;
  /** Motivo por el que el selector está bloqueado (p.ej. el usuario ya existe). */
  disabledHelp?: string;
}

const AuthMethodSelect: React.FC<Props> = ({
  value,
  onChange,
  subject = 'owner',
  disabled = false,
  disabledHelp,
}) => {
  const { t } = useTranslation();

  const help = () => {
    if (disabled && disabledHelp) return disabledHelp;
    if (value === 'google') {
      return subject === 'partner'
        ? t(
            'authMethod.googleHelpPartner',
            'Entrará con su cuenta de Google: no se genera contraseña y recibirá un correo de bienvenida explicándoselo.'
          )
        : t(
            'authMethod.googleHelpOwner',
            'Entrará con su cuenta de Google: no se genera contraseña y recibirá un correo de bienvenida explicándoselo.'
          );
    }
    if (value === 'password_google') {
      return t(
        'authMethod.bothHelp',
        'Podrá entrar con la contraseña de un solo uso que te mostraremos o con su cuenta de Google.'
      );
    }
    return t(
      'authMethod.passwordHelp',
      'Se genera una contraseña de un solo uso que te mostraremos aquí y se le enviará por correo.'
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth variant="outlined" margin="dense" disabled={disabled}>
        <InputLabel>{t('authMethod.label', 'Cómo entrará')}</InputLabel>
        <Select
          value={value}
          label={t('authMethod.label', 'Cómo entrará')}
          onChange={(e) => onChange(e.target.value as AccountUserAuthMethod)}
        >
          <MenuItem value="password">{t('authMethod.password', 'Contraseña')}</MenuItem>
          <MenuItem value="password_google">
            {t('authMethod.passwordGoogle', 'Contraseña y Google')}
          </MenuItem>
          <MenuItem value="google">{t('authMethod.google', 'Solo Google')}</MenuItem>
        </Select>
      </FormControl>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {help()}
      </Typography>
    </Box>
  );
};

export default AuthMethodSelect;
